import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";

import TableFilters from "../../TableFilters";
import Timer from "../../Timer";
import {
  addStateRisk,
  editStateRisk,
  riskFetching,
  riskFilterText,
  rowEditRisk,
  selectedRowsStateRisk,
} from "../atoms/RiskAtoms";
import RiskForm from "../forms/RiskForm";
import useDisableRisk from "../hooks/useDisableRisk";
import useGetRisk from "../hooks/useGetRisk";
import { RiskDataType } from "../types/RiskInterface";

export default function RiskTable() {
  const {
    data: directData,
    isSuccess: dataSuccess,
    refetch: reFetch,
  } = useGetRisk();

  const BulkDelete = useDisableRisk();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateRisk);
  const [selectedRows, setSelectedRows] = useRecoilState(selectedRowsStateRisk);
  const [actionEditState, setActionEditState] = useRecoilState(editStateRisk);
  const [filterTextState, setFilterTextState] = useRecoilState(riskFilterText);
  const setEditRowData = useSetRecoilState(rowEditRisk);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(riskFetching);

  //Local States
  const [tableData, setTableData] = useState<RiskDataType[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<RiskDataType[]>(
    []
  );

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_risk_add;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_risk_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_risk_delete;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  useEffect(() => {
    if (dataSuccess && directData) {
      Loader();
    }
  }, [directData, dataSuccess]);

  //For filtered data update
  const [dataId, setDataId] = useState("");
  useEffect(() => {
    if (dataId !== "") {
      if (dataId?.includes(".")) {
        let ids = dataId.split(".");
        const arr = ids.map((id) => Number(id));
        const filteredData = tableData?.filter((x) => arr.includes(x.riskId));
        setTableFilteredData(filteredData);
      } else {
        const filteredData = tableData?.filter(
          (x) => x.riskId === Number(dataId)
        );
        setTableFilteredData(filteredData);
      }
    }
  }, [dataId, tableData]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setTableFilteredData(result);
          setDataId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.riskId);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Risks" : "Risk"
    }?`}</div>
  );

  const filteredItems = tableData
    ? tableData?.filter(
        (item) =>
          item.riskName &&
          item.riskName
            .toLowerCase()
            .includes(filterTextState?.trim().toLowerCase())
      )
    : [];
  const matColumns = [
    {
      title: "Risk Name",
      field: "riskName",
      customSort: (a, b) =>
        a.riskName && b.riskName
          ? a.riskName.trim().localeCompare(b.riskName.trim())
          : a.riskName
          ? 0
          : -1,
    },

    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <RiskForm reFetch={reFetch} />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                default={filterTextState}
                moduleDetails={"Risk"}
                textInputName={"name"}
                columnDetails={"Risk Name"}
                filterText={setFilterTextState}
                disabledButton={
                  selectedRows !== undefined && selectedRows.length > 0
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New Risk"
                      onClick={() => handleAdd()}
                      disabled={
                        selectedRows !== undefined && selectedRows.length > 0
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>

            <div>
              <MaterialTableTS
                data={
                  isFetching
                    ? []
                    : tableFilteredData?.length > 0
                    ? tableFilteredData
                    : filterTextState === ""
                    ? tableData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={userPermissions.includes(deletePermission)}
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Risk"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
}
